import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { createColumnHelper } from '@tanstack/react-table';
import DataTable from '@/components/functional/DataTable/DataTable';
import { TEMPLATES } from '@/constants/templates';

interface TemplateRow {
  id: string;
  name: string;
  action: string;
}

const columnHelper = createColumnHelper<TemplateRow>();

const createColumns = (t: (key: string) => string, onActionClick: (row: TemplateRow) => void) => [
  columnHelper.accessor('name', {
    cell: (info) => <span className='font-semibold'>{info.getValue()}</span>,
    header: () => <span>{t('templates.capacityDomain')}</span>,
    enableSorting: false,
  }),
  columnHelper.accessor('action', {
    header: () => <span>{t('templates.action')}</span>,
    cell: (info) => (
      <span 
        className='font-bold underline cursor-pointer transition-colors hover:text-blue-600'
        onClick={() => onActionClick(info.row.original)}
      >
        {info.renderValue()}
      </span>
    ),
    enableSorting: false,
  }),
];

const TemplatesPage: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleActionClick = (row: TemplateRow) => {
    navigate(`/templates/${row.id}`);
  };

  const templatesData = Object.values(TEMPLATES).map(template => ({
    id: template.id,
    name: template.name,
    action: t('templates.showTemplate'),
  }));

  const columns = createColumns(t, handleActionClick);

  return (
    <div className="flex flex-col gap-4">
      <div className='text-4xl bg-white rounded-2xl px-6 py-4 font-bold shadow-assessment-sm'>
        {t('templates.capacityBuildingPlanTemplates')}
      </div>

      <DataTable 
        data={templatesData} 
        columns={columns} 
        className="[&_tbody_tr:nth-child(odd)]:bg-table-stripe" 
      />

      {templatesData.length === 0 && (
        <div className="text-center py-12 bg-white rounded-2xl shadow-assessment-sm">
          <div className="text-gray-500">
            <div className="text-lg font-medium mb-2">
              {t('templates.noTemplatesFound')}
            </div>
            <div className="text-sm">
              {t('templates.noTemplatesDescription')}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;